import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { AuditActorSummary, AuditStamp, auditActorLabel } from './audit.models';

@Component({
  selector: 'app-audit-hero-strip',
  standalone: true,
  host: { '[style.display]': 'entries.length ? null : "none"' },
  imports: [CommonModule, RouterModule],
  template: `
    <div class="audit-strip" aria-label="Record audit">
      @for (entry of entries; track entry.label) {
        <div class="audit-entry">
          <span class="audit-label">{{ entry.label }}</span>
          <div class="audit-person">
            @if (entry.id && canViewProfiles) {
              <a class="actor" [routerLink]="['/customer', entry.id]" [attr.aria-label]="'View ' + entry.name + ' profile'">
                <ng-container *ngTemplateOutlet="avatar; context: { $implicit: entry }"></ng-container>
                <span>{{ entry.name }}</span>
              </a>
            } @else {
              <span class="actor">
                <ng-container *ngTemplateOutlet="avatar; context: { $implicit: entry }"></ng-container>
                <span>{{ entry.name }}</span>
              </span>
            }
            <time>{{ entry.at ? (entry.at | date:'short') : 'Not recorded' }}</time>
          </div>
        </div>
      }
    </div>
    <ng-template #avatar let-entry>
      @if (entry.imageUrl) {
        <img class="avatar" [src]="entry.imageUrl" alt="" />
      } @else {
        <span class="avatar avatar-fallback" aria-hidden="true">{{ initials(entry.name) }}</span>
      }
    </ng-template>
  `,
  styles: [`
    .audit-strip { display: grid; gap: .35rem; width: max-content; max-width: 100%; padding: .4rem .55rem; border-radius: 6px; background: var(--app-surface-soft, #f8fafc); }
    .audit-entry { display: grid; grid-template-columns: 4rem minmax(0, 1fr); align-items: center; gap: .5rem; min-width: 0; }
    .audit-label { color: var(--app-muted, #64748b); font-size: .7rem; font-weight: 700; text-transform: uppercase; }
    .audit-person { display: flex; align-items: center; flex-wrap: wrap; gap: .2rem .4rem; min-width: 0; }
    .actor { display: inline-flex; align-items: center; gap: .3rem; min-width: 0; color: var(--app-text, #172033); font-size: .76rem; font-weight: 600; text-decoration: none; }
    a.actor:hover { text-decoration: underline; }
    a.actor:focus-visible { outline: 2px solid var(--app-accent, #0ea5a3); outline-offset: 2px; border-radius: 4px; }
    .actor span:last-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 9rem; }
    .avatar { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; width: 1.35rem; height: 1.35rem; border-radius: 50%; object-fit: cover; }
    .avatar-fallback { background: var(--app-accent-surface, #ecfdf5); color: var(--app-accent-strong, #0f766e); font-size: .57rem; font-weight: 700; }
    time { color: var(--app-muted, #64748b); font-size: .72rem; white-space: nowrap; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuditHeroStripComponent {
  @Input() stamp?: AuditStamp | null;

  private readonly permissions = inject(PermissionService);

  get canViewProfiles(): boolean {
    return this.permissions.hasPermission(app_permissions.customer.records.view);
  }

  get entries(): { label: string; at?: Date | string | number | null; id?: number | null; name: string; imageUrl?: string | null }[] {
    const stamp = this.stamp;
    if (!stamp || (!stamp.showWhenEmpty && !Object.values(stamp).some(value => value != null))) return [];
    const entry = (label: string, at: Date | string | number | null | undefined, actor: AuditActorSummary | null | undefined, source?: string | null) => ({
      label, at, id: actor?.id, name: auditActorLabel(actor, undefined, source), imageUrl: actor?.imageUrl
    });
    const entries = [
      entry('Created', stamp.createdAt, stamp.createdByActor, stamp.createdAuditSource),
      entry('Updated', stamp.updatedAt, stamp.updatedByActor, stamp.updatedAuditSource)
    ];
    if (stamp.deletedAt) entries.push(entry('Deleted', stamp.deletedAt, stamp.deletedByActor));
    return entries;
  }

  initials(name: string): string {
    if (name === 'Not recorded') return '?';
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join('');
  }
}
